import { i18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { portalLanguagesData } from 'app/appSettings'
import { setUserData } from 'app/redux/actions/UserActions'
import * as jsonCatalogEn from 'locales/en/messages.json'
import * as jsonCatalogFr from 'locales/fr/messages.json'
import * as jsonCatalogIuCans from 'locales/iu-cans/messages.json'
import moment from 'moment'
import 'moment/locale/fr'
import React, { useCallback, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { updateCatalog } from '../app/redux/actions/I18nActions'

export const languages = Object.values(portalLanguagesData).map(
  lang => lang.editKey
)

export const languagesWithPortalTranslationId = [
  ...languages,
  'portalTranslationId'
]

export const jsonCatalogs = {
  en: jsonCatalogEn.default,
  fr: jsonCatalogFr.default,
  'iu-cans': jsonCatalogIuCans.default
}

export const myI18n = i18n

export const userToFormat = user => {
  if (user && (user.language === 'fr' || user.language === 'fr_CA')) {
    return 'DD/MM/YYYY'
  } else {
    return 'YYYY-MM-DD'
  }
}

const I18nConnectedProvider = ({
  language,
  user,
  children,
  loadedCatalogs = {},
  updateCatalog,
  setUserData
}) => {
  const [loaded, setLoaded] = useState(false)
  const [mergedCatalogs, setMergedCatalogs] = useState(null)

  const updateLanguage = useCallback(
    nextLanguage => {
      if (!nextLanguage || nextLanguage === language) {
        return
      }

      moment.locale(nextLanguage)
      myI18n.activate(nextLanguage)
      i18n.activate(nextLanguage)
      setUserData({ ...(user ?? {}), language: nextLanguage })
    },
    [language, setUserData, user]
  )

  useEffect(() => {
    if (!loaded) return
    if (language && myI18n.locale !== language) {
      myI18n.activate(language)
      i18n.activate(language)
      moment.locale(language)
    }
  }, [language, loaded])

  useEffect(() => {
    /*
    chunks generated by these files will later have their content replaced
    by the translation script
    their content is still important since it is used to select what format of content
    should be inside
    also for lingui 4+ content format they are used to determine which id is  explicit,
    the rest are hashed

    !!! the files should be regenerated each time a new explicit id message is
    used
    */

    Promise.all([
      import(
        /* webpackChunkName: "messagesEn" */ '../locales/en/messages.js'
      ),
      import(
        /* webpackChunkName: "messagesFr" */ '../locales/fr/messages.js'
      ),
      import(
        /* webpackChunkName: "messagesIuCans" */ '../locales/iu-cans/messages.js'
      )
    ])
      .then(
        ([
          catalogEnDynamic,
          catalogFrDynamic,
          catalogIuCansDynamic
        ]) => {
          console.log('Loaded catalogs from chunks: ', catalogEnDynamic, catalogFrDynamic, language)

          const loadedFr = catalogFrDynamic.default
          const loadedEn = catalogEnDynamic.default
          const loadedIu = catalogIuCansDynamic.default

          const loadedCatalogs = {
            en: loadedEn,
            fr: loadedFr,
            'iu-cans': loadedIu
          }

          const mergedCatalogs = {}
          Object.keys(loadedCatalogs).forEach(key => {
            const lang = key.split('_')[0]
            mergedCatalogs[key] = loadedCatalogs[lang]
            myI18n.load(key, mergedCatalogs[key].messages)
            i18n.load(key, mergedCatalogs[key].messages)
          })
          setMergedCatalogs(mergedCatalogs)
          myI18n.activate(language ?? 'en')
          i18n.activate(language ?? 'en')
          setLoaded(true)
        }
      )
      .catch(e => {
        console.warn('catalog localeSplit failed loading messages', e)
      })
  }, [])

  if (!loaded) {
    return null
  }

  return (
    <CatalogContext.Provider value={{
      catalogs: mergedCatalogs,
      language,
      updateLanguage
    }}
    >
      <I18nProvider i18n={myI18n}>
        <LocalizationProvider
          dateAdapter={AdapterMoment}
          dateLibInstance={moment}
          adapterLocale={language}
        >
          {children}
        </LocalizationProvider>
      </I18nProvider>
    </CatalogContext.Provider>
  )
}

export function useLinguiCatalogsContext () {
  const value = React.useContext(CatalogContext)
  return value
}

export function useI18nConnectedContext () {
  const value = React.useContext(CatalogContext)
  return value
}

const CatalogContext = React.createContext({
  catalogs: null,
  language: 'en',
  updateLanguage: () => {}
})

const mapStateToProps = state => ({
  language: state.user.language || 'en',
  user: state.user,
  loadedCatalogs: state.i18n
})
export default connect(mapStateToProps, { updateCatalog, setUserData })(
  I18nConnectedProvider
)
